/* Sync Status panel — file list grouped by sync direction with per-file actions */

import { state, esc } from './state.js';
import { api } from './api.js';
import { getPanelContainer, showPanel } from './panel-registry.js';
import { loadSyncStatuses, loadFileTree, pullWithFeedback, pushWithFeedback, bulkSync, KIND_TO_API } from './file-tree.js';
import { openDiffForScript } from './diff-panel.js';
import { renderDiffInto } from './diff-render.js';

const KIND_LABELS = {
  "global-scripts": "Script",
  "custom-calculations": "Calculation",
  "ui-templates": "Template",
  "quote-tables": "Quote Table",
};

// Track resolved items within a session so the UI can flash green
const _resolved = new Set();

// Track collapsed groups — conflicts and remote start expanded, in_sync starts collapsed
const _collapsed = new Set(['in_sync']);

// Lucide icon classes for action buttons (see icons.css).
const ICONS = {
  diff: '<i class="ic ic-git-pull-request ico"></i>',
  pull: '<i class="ic ic-arrow-down-to-line ico"></i>',
  push: '<i class="ic ic-arrow-up-from-line ico"></i>',
  keep: '<i class="ic ic-check ico"></i>',
};

/**
 * Populate the sync-status panel DOM. Called once during init.
 */
export function populateSyncPanel() {
  const panel = getPanelContainer('sync-status');
  if (!panel) return;

  panel.innerHTML = `
    <div class="sync-status-panel">
      <div class="sync-status-header">
        <h3 class="sync-status-title">Sync Status</h3>
        <button class="btn-icon" id="btnRefreshSyncPanel" title="Refresh">
          <i class="ic ic-refresh-cw"></i>
        </button>
      </div>
      <div class="sync-status-groups" id="syncPanelGroups"></div>
      <div class="sync-status-footer" id="syncPanelFooter"></div>
    </div>
  `;

  document.getElementById('btnRefreshSyncPanel')?.addEventListener('click', async () => {
    await loadSyncStatuses(true);
    renderSyncPanel();
  });
}

/**
 * Render the sync panel contents from state.syncStatuses.
 * Safe to call any time — re-reads state and rebuilds DOM.
 */
export function renderSyncPanel() {
  const groupsEl = document.getElementById('syncPanelGroups');
  const footerEl = document.getElementById('syncPanelFooter');
  if (!groupsEl || !footerEl) return;

  const statuses = state.syncStatuses || [];
  const groups = {
    conflict:    statuses.filter(s => s.direction === 'conflict'),
    remote_only: statuses.filter(s => s.direction === 'remote_only'),
    local_only:  statuses.filter(s => s.direction === 'local_only'),
    in_sync:     statuses.filter(s => s.direction === 'in_sync'),
  };

  // Groups
  groupsEl.innerHTML = '';
  _renderGroup(groupsEl, 'conflict',    'Conflicts',   groups.conflict,    '<i class="ic ic-sm ic-alert-triangle"></i>');
  _renderGroup(groupsEl, 'remote_only', 'Remote only', groups.remote_only, '<i class="ic ic-sm ic-arrow-down"></i>');
  _renderGroup(groupsEl, 'local_only',  'Local only',  groups.local_only,  '<i class="ic ic-sm ic-arrow-up"></i>');
  _renderGroup(groupsEl, 'in_sync',     'In sync',     groups.in_sync,     '<i class="ic ic-sm ic-check"></i>');

  // Footer bulk actions
  const hasRemote = groups.remote_only.length > 0 || groups.conflict.length > 0;
  const hasLocal = groups.local_only.length > 0;
  footerEl.innerHTML = '';
  if (hasRemote || hasLocal) {
    if (hasRemote) {
      const btn = document.createElement('button');
      btn.className = 'btn btn-xs';
      btn.innerHTML = '↓ Pull All';
      btn.title = 'Pull all remote and resolve conflicts by pulling';
      btn.addEventListener('click', () => _bulkPull(groups));
      footerEl.appendChild(btn);
    }
    if (hasLocal) {
      const btn = document.createElement('button');
      btn.className = 'btn btn-xs';
      btn.innerHTML = '↑ Push All';
      btn.title = 'Push all local-only items';
      btn.addEventListener('click', () => _bulkPush(groups));
      footerEl.appendChild(btn);
    }
  }
}

const DIRECTION_BADGE = {
  conflict:    'sps-conflict',
  remote_only: 'sps-remote',
  local_only:  'sps-local',
  in_sync:     'sps-ok',
};

function _renderGroup(container, direction, label, items) {
  if (items.length === 0) return;

  const isCollapsed = _collapsed.has(direction) ||
    (direction === 'local_only' && items.length >= 10 && !_userToggled.has(direction));

  const group = document.createElement('div');
  group.className = 'sps-group';
  group.dataset.direction = direction;

  const badgeClass = DIRECTION_BADGE[direction] || '';
  const header = document.createElement('div');
  header.className = 'panel-group-header sps-group-header';
  header.innerHTML = `<span class="panel-caret ${isCollapsed ? '' : 'open'}">&#9654;</span> ${label} <span class="sps-badge ${badgeClass}">${items.length}</span>`;
  header.addEventListener('click', () => {
    if (_collapsed.has(direction)) {
      _collapsed.delete(direction);
    } else {
      _collapsed.add(direction);
    }
    _userToggled.add(direction);
    renderSyncPanel();
  });
  group.appendChild(header);

  if (!isCollapsed) {
    const list = document.createElement('div');
    list.className = 'sps-file-list';
    for (const item of items) {
      list.appendChild(_renderFileRow(item, direction));
    }
    group.appendChild(list);
  }

  container.appendChild(group);
}

// Track which groups the user has manually toggled
const _userToggled = new Set();

// Rows the user has expanded to show an inline diff (co-located review).
const _expandedSync = new Set();

function _renderFileRow(item, direction) {
  const wrap = document.createElement('div');
  wrap.className = 'sps-file-wrap';
  const key = `${item.kind}/${item.name}`;
  const expanded = _expandedSync.has(key);

  const row = document.createElement('div');
  row.className = `sps-file-row${_resolved.has(key) ? ' sps-resolved' : ''}`;
  row.setAttribute('role', 'button');
  row.title = 'Show the diff inline';

  const kindLabel = KIND_LABELS[item.kind] || item.kind;
  const caret = `<span class="sps-caret${expanded ? ' open' : ''}">&#9654;</span>`;
  const nameSpan = `<span class="sps-file-name">${esc(item.name)}</span>`;
  const kindSpan = `<span class="sps-file-kind">${esc(kindLabel)}</span>`;

  const actions = document.createElement('div');
  actions.className = 'sps-actions';

  if (direction === 'conflict') {
    actions.appendChild(_iconBtn('diff', 'Open full diff', () => _openDiff(item)));
    actions.appendChild(_iconBtn('pull', 'Pull from cloud', () => _pullItem(item)));
    actions.appendChild(_iconBtn('keep', 'Keep local', () => _keepItem(item)));
  } else if (direction === 'remote_only') {
    actions.appendChild(_iconBtn('pull', 'Pull from cloud', () => _pullItem(item)));
  } else if (direction === 'local_only') {
    actions.appendChild(_iconBtn('push', 'Push to cloud', () => _pushItem(item)));
  }

  row.innerHTML = `${caret}${nameSpan} ${kindSpan}`;
  row.appendChild(actions);
  // Row click toggles the inline diff (action buttons stopPropagation already).
  row.addEventListener('click', () => _toggleSyncDiff(item));
  wrap.appendChild(row);

  if (expanded) {
    const body = document.createElement('div');
    body.className = 'sps-diff-body';
    _populateSyncDiffBody(body, item);
    wrap.appendChild(body);
  }
  return wrap;
}

function _toggleSyncDiff(item) {
  const key = `${item.kind}/${item.name}`;
  if (_expandedSync.has(key)) _expandedSync.delete(key);
  else _expandedSync.add(key);
  renderSyncPanel();
}

async function _populateSyncDiffBody(body, item) {
  const cacheKey = `${item.kind}/${item.name}`;
  const cached = state.diffCache.get(cacheKey);
  if (cached) { renderDiffInto(body, cached); return; }
  body.innerHTML = `<div class="diff-empty">Loading diff…</div>`;
  try {
    const apiKind = KIND_TO_API[item.kind] || item.kind;
    const result = await api.syncDiff(apiKind, item.name);
    state.diffCache.set(cacheKey, result);
    renderDiffInto(body, result);
  } catch (e) {
    body.innerHTML = `<div class="diff-empty diff-error">Error: ${esc(e.message)}</div>`;
  }
}

function _iconBtn(type, title, onClick) {
  const btn = document.createElement('button');
  btn.className = 'btn-icon sps-action-btn';
  btn.title = title;
  btn.innerHTML = ICONS[type];
  btn.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
  return btn;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

function _openDiff(item) {
  const apiKind = KIND_TO_API[item.kind] || item.kind;
  openDiffForScript(item.name, apiKind);
}

async function _pullItem(item) {
  const apiKind = KIND_TO_API[item.kind] || item.kind;
  const ok = await pullWithFeedback(apiKind, item.name);
  if (ok) {
    state.diffCache.delete(`${item.kind}/${item.name}`); // stale after pull
    _resolved.add(`${item.kind}/${item.name}`);
    renderSyncPanel();
    setTimeout(() => { _resolved.delete(`${item.kind}/${item.name}`); }, 1500);
  }
}

async function _pushItem(item) {
  const apiKind = KIND_TO_API[item.kind] || item.kind;
  const ok = await pushWithFeedback(apiKind, item.name);
  if (ok) {
    state.diffCache.delete(`${item.kind}/${item.name}`); // stale after push
    _resolved.add(`${item.kind}/${item.name}`);
    renderSyncPanel();
    setTimeout(() => { _resolved.delete(`${item.kind}/${item.name}`); }, 1500);
  }
}

async function _keepItem(item) {
  _resolved.add(`${item.kind}/${item.name}`);
  renderSyncPanel();
  setTimeout(() => { _resolved.delete(`${item.kind}/${item.name}`); }, 1500);
}

async function _bulkPull(groups) {
  const items = [...groups.remote_only, ...groups.conflict];
  await bulkSync('pull', items, { reloadTree: true });
  renderSyncPanel();
}

async function _bulkPush(groups) {
  await bulkSync('push', groups.local_only);
  renderSyncPanel();
}

// ---------------------------------------------------------------------------
// Status bar sync indicator
// ---------------------------------------------------------------------------

/** Previous sync state — used to detect changes for flash animation */
let _prevSyncState = null;

/**
 * Update the status bar sync indicator segment.
 * @param {string} [syncingText] — if provided, shows syncing state with this text
 */
export function updateStatusBarSync(syncingText) {
  const el = document.getElementById('sbSyncIndicator');
  if (!el) return;

  const textEl = el.querySelector('.sb-sync-text');

  if (syncingText) {
    el.className = 'sb-sync sb-sync-syncing';
    if (textEl) textEl.textContent = syncingText;
    el.title = 'Syncing with cloud...';
    return;
  }

  if (!state.tenant) {
    el.className = 'sb-sync sb-sync-offline';
    if (textEl) textEl.textContent = 'No tenant';
    el.title = 'No tenant connected';
    return;
  }

  const statuses = state.syncStatuses || [];
  if (statuses.length === 0) {
    el.className = 'sb-sync sb-sync-offline';
    if (textEl) textEl.textContent = `Connected to ${state.tenant}`;
    el.title = 'Connected — no sync data yet';
    return;
  }

  const conflicts   = statuses.filter(s => s.direction === 'conflict').length;
  const remoteOnly  = statuses.filter(s => s.direction === 'remote_only').length;
  const localOnly   = statuses.filter(s => s.direction === 'local_only').length;
  const synced      = statuses.filter(s => s.direction === 'in_sync').length;

  let stateClass;
  const parts = [];

  if (conflicts > 0) {
    stateClass = 'sb-sync-conflict';
    parts.push(`! ${conflicts} conflict${conflicts > 1 ? 's' : ''}`);
  }
  if (remoteOnly > 0) {
    if (!stateClass) stateClass = 'sb-sync-remote';
    parts.push(`↓ ${remoteOnly} to pull`);
  }
  if (localOnly > 0) {
    if (!stateClass) stateClass = 'sb-sync-local';
    parts.push(`↑ ${localOnly} to push`);
  }
  if (parts.length === 0) {
    stateClass = 'sb-sync-ok';
    parts.push(`✓ ${synced} synced`);
  }

  const text = parts.slice(0, 2).join(', ');
  const title = parts.join(', ');

  el.className = `sb-sync ${stateClass}`;
  if (textEl) textEl.textContent = text;
  el.title = title;

  const newState = stateClass;
  if (_prevSyncState && _prevSyncState !== newState) {
    el.classList.add('sb-sync-flash');
    setTimeout(() => el.classList.remove('sb-sync-flash'), 500);
  }
  _prevSyncState = newState;
}

/**
 * Focus the sync panel and expand a specific group.
 * @param {string} [direction] — 'conflict', 'remote_only', 'local_only', 'in_sync'
 */
export function focusSyncPanel(direction) {
  showPanel('sync-status');
  renderSyncPanel();
  // Auto-fetch if no data yet
  if (!state.syncStatuses || state.syncStatuses.length === 0) {
    loadSyncStatuses(true);
  }
  if (direction) {
    _collapsed.delete(direction);
    renderSyncPanel();
    setTimeout(() => {
      const group = document.querySelector(`.sps-group[data-direction="${direction}"]`);
      if (group) group.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 50);
  }
}
